import type { Logger } from "@expert/logging";

import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { type TimelineFeatures, useFeatures } from "@expert/features";
import { Anchor, type AnchorProps, Tooltip } from "@mantine/core";

import type { DATASET_MESSAGE_ID_ATTRIBUTE_KEY } from "../../processing";

import { useOzmoStore } from "../../ozmo";

export interface OzmoLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
    [DATASET_MESSAGE_ID_ATTRIBUTE_KEY]: string;
    actionId: string;
    sessionId: string;
    logger?: Logger;
    ozmo?: boolean;
}

// TODO: [EWS-1376](https://solutonashville.atlassian.net/browse/EWS-1376)
// If the ozmo iframe implementation pilot is successful and we get rid
// of the old new tab implementation, we can revisit how this component works
export function OzmoLink({
    "data-message-id": messageId,
    actionId,
    children,
    href,
    logger,
    ozmo,
    sessionId: currentSessionId,
    title,
}: OzmoLinkProps) {
    const {
        features: { isOzmoIFrameEnabled },
    } = useFeatures<TimelineFeatures>();

    const { dispatcher } = useReactAnalytics();
    const { setIsOzmoOpened } = useOzmoStore();

    const onClick = () => {
        const eventName = ozmo ? `Ozmo_${actionId}` : `Click_${actionId || "Link"}`;
        logger?.child({ module: "OzmoLink" }).info({ url: href, title, messageId }, `Ozmo | ${eventName} clicked`);
        void dispatcher.dispatchBusinessEvent(eventName, {
            source: "Message",
            url: href,
            actionId: actionId || title,
            title,
            messageId,
        });
        if (ozmo && isOzmoIFrameEnabled)
            setIsOzmoOpened({ currentSessionId, isOpened: true, dispatcher, logger, source: "OzmoLink" });
    };

    const baseAnchorProps = {
        onClick,
        size: "sm",
        underline: "always",
        "data-testid": "OzmoLink",
    } as AnchorProps;

    const regularLinkProps = {
        href,
        target: "_blank",
    };

    const anchorProps =
        ozmo && isOzmoIFrameEnabled
            ? {
                  ...baseAnchorProps,
              }
            : {
                  ...baseAnchorProps,
                  ...regularLinkProps,
              };

    return (
        <Tooltip
            label={href}
            position="top-start"
            zIndex={999}
            multiline
            maw="20rem"
            styles={{
                tooltip: {
                    overflowWrap: "break-word",
                },
            }}
        >
            <Anchor {...anchorProps}>{children}</Anchor>
        </Tooltip>
    );
}
